import { Name } from '@wix/ambassador-account-info-server/types';
import { Article } from '@wix/answers-api';
import { StartChatInitialData, StatusBanner, WidgetSession } from '@wix/wix-chatbot-common/browser';
import type { ArticleData, HomepageContent } from '@wix/wix-help-widget-common/homepage';

import { ACTIONS, MODAL, UI } from '../../../constants';
import { TLocalesKeys } from '../../../types';
import { CategoryItemType } from '../api/getCategories';
import { SearchArticlesItem } from '../api/searchArticles';
import {
  IAgent,
  IAIArticleAnswer,
  IArticle,
  IArticleSelector,
  IBotMessage,
  IBotTitleMessage,
  IBotTyping,
  IButtons,
  ICategory,
  IChatTransferred,
  IDomainSelector,
  IImage,
  IIntentSelector,
  IInvoiceSelector,
  ILiveChatAgentStatus,
  ILiveChatChangeMode,
  ILiveChatMessage,
  ILoginSelector,
  IPhone,
  IPlanSelector,
  IScheduledCallbacksListSelector,
  ISiteSelector,
  ISuggestionSelector,
  ITicketSelector,
  ITimeslotSelector,
  IUserAccountsSelector,
  IUserMessage,
  IVideo,
} from '../messageCreators';
import { IWaitingForAgent } from '../messageCreators/waitingForAgent';
import { StartChatParams } from '../thunks/chatbotStartChat.thunk';
import { TRootState } from './index';

interface GenericMessageItem {
  clientOnly?: boolean;
}

export type TItem = GenericMessageItem &
  (
    | IArticle
    | IAIArticleAnswer
    | IBotMessage
    | IBotTitleMessage
    | IBotTyping
    | IUserMessage
    | IButtons
    | IPhone
    | IVideo
    | IImage
    | ICategory
    | ISiteSelector
    | IDomainSelector
    | IPlanSelector
    | IArticleSelector
    | IInvoiceSelector
    | ISuggestionSelector
    | ITicketSelector
    | ILoginSelector
    | ITimeslotSelector
    | IScheduledCallbacksListSelector
    | IUserAccountsSelector
    | IIntentSelector
    // Live Chat
    | ILiveChatMessage
    | ILiveChatChangeMode
    | ILiveChatAgentStatus
    | IChatTransferred
    | IWaitingForAgent
  );

export interface IUserAction {
  type: UI;
  detail: {
    messageId: string;
    changeId?: string;
    [key: string]: unknown;
  };
}

export interface IUndelivered {
  readonly [key: string]: IUserAction;
}

export enum ChatScrollToPositions {
  Bottom,
}

export type ChatScrollTo = {
  position: ChatScrollToPositions;
};

export type RestartChatParameters = Readonly<{
  suggestionId?: string;
  nodeId?: string;
}>;

export interface IChatState {
  readonly isLocked: boolean;
  readonly isLiveChat: boolean;
  readonly isMuted: boolean;
  readonly conversationId?: string;
  readonly items: TItem[];
  readonly undelivered: IUndelivered;
  readonly unreadCount: number;
  readonly scrollTo?: ChatScrollTo;
  readonly startChatLoading?: boolean;
  readonly reloadStartChat?: boolean;
  readonly startChatError?: string;
  readonly startChatParameters?: StartChatParams;
  readonly restartChatParameters?: RestartChatParameters;
  readonly chatbotDataLoading?: boolean;
  readonly isReset?: boolean;
  readonly startingNodeId?: string;
}

export interface IAttachment {
  readonly name: string;
  readonly url: string;
  readonly width: number;
  readonly height: number;
}

export interface IAttachmentError {
  localKey: TLocalesKeys;
  fileNames?: string[];
  learnMoreUrl?: string;
}

export interface IAttachesState {
  readonly attachments: IAttachment[];
  readonly error: IAttachmentError | null;
  readonly isLoading: boolean;
}

export interface IFieldState {
  readonly maxLength: number;
  readonly disabled: boolean;
  readonly hidden: boolean;
  readonly placeholder: string | undefined;
  readonly value: string;
  readonly attachFile: boolean;
  readonly multiline: boolean;
  readonly nodeId: string | undefined;
  readonly change: boolean | undefined;
}

export interface IAnnouncementState {
  readonly label?: string;
  readonly url?: string;
  readonly errorMsg?: string;
}

export interface IHeaderState {
  readonly isEnable: boolean;
  readonly announcement: IAnnouncementState | undefined;
  readonly onlineAgents: IAgent[];
  readonly statusBanner?: StatusBanner;
  readonly hideStatusBanner: boolean;
}

export interface IModalState {
  type: MODAL | null;
}

export interface IWidgetSessionState {
  readonly sessionTabId: string;
  readonly session?: WidgetSession;
  readonly addedUrl?: string;
  readonly replacedUrl?: string;
  readonly removeLast?: boolean;
  readonly isSessionExpired: boolean;
}

export interface IUserState {
  readonly countryCode: string;
  readonly id: string;
  readonly profileImage: string;
  readonly name?: Name;
  readonly email: string;
  readonly phoneNumbers?: string[];
}

export interface IInputsFocusState {
  readonly search: boolean;
}

export enum PresentedState {
  SearchResults = 'Search Results',
  SingleArticle = 'Single Article',
  EmptyHomePage = 'Empty Home Page',
  Chatbot = 'Chatbot',
  AllHelpTopics = 'All Help Topics',
  SubCategoriesList = 'Sub Categories List',
  SolutionList = 'Solution List',
  Homepage = 'Homepage',
  WidgetButton = 'Widget Button',
  RequestSuccessManagerForm = 'Request Success Manager Form',
}

export interface IHelpCenterState {
  readonly currentCategory: CategoryItemType | null;
  readonly currentArticle: Article | null;
  readonly currentArticleLoading: boolean;
  readonly categories?: CategoryItemType[];
  readonly normalizedCategories: Record<CategoryItemType['id'], CategoryItemType>;
  readonly articleHeadingVisible: boolean;
  readonly fullPageSidebarOpened: boolean;
  readonly fullPageSidebarExtended: boolean;
  readonly searchedText: string | undefined;
  readonly currentPresentedState: PresentedState | undefined;
  readonly previousPresentedState: PresentedState | undefined;
}

export interface ISolutionsState {
  readonly query: string;
  readonly solutions: TSolution[] | null;
  readonly error: string | null;
  readonly isLoading: boolean;
  readonly isUserTypedSearch: boolean;
  readonly paging: {
    hasMore: boolean;
  };
}

export interface IHomepageSectionState<T> {
  data: null | T;
  loading: boolean;
  error: string | null;
}

export interface IHomepageState {
  scrolledToTop: boolean;
  content: IHomepageSectionState<HomepageContent>;
  articles: IHomepageSectionState<{
    articles: ArticleData[];
    modelId?: string;
  }>;
  reportABugFlowStarted: boolean;
}

export interface IBiEngagementState {
  mouseClick: boolean;
  mouseMove: boolean;
  scroll: boolean;
  scrollToBottom: boolean;
  scrollAddedContent: boolean;
  maxScrollAtFirstPosition: boolean;
  scrollPercentage: number; // percentage 0-100
}

// Modal actions
export interface IModalOpenAction {
  type: ACTIONS.MODAL_OPEN;
  data: MODAL;
}

export interface IModalCloseAction {
  type: ACTIONS.MODAL_CLOSE;
}

export type TModalActions = IModalOpenAction | IModalCloseAction;

// Inputs focus IInputsFocusState
export interface IInputsFocusSearchAction {
  type: ACTIONS.INPUTS_FOCUS_SEARCH;
  search: boolean;
}

export type TInputsFocusActions = IInputsFocusSearchAction;

// meta
export type DealerOfferParams = Map<string, string>;

export interface IMetaState {
  readonly loadPageUrl: string;
  readonly startUrl: string;
  readonly origPlatform: string;
  readonly origPageName: string;
  readonly mainOptionName?: string;
  readonly intentId?: string;
  readonly dealerOfferParams?: DealerOfferParams;
}

export interface IMetaUpdatePageUrl {
  readonly type: ACTIONS.META_UPDATE_PAGE_URL;
  readonly loadPageUrl: string;
  readonly startUrl: string;
}

export interface IMetaUpdateOrigParams {
  readonly type: ACTIONS.META_UPDATE_ORIG_PARAMS;
  readonly origPlatform: string;
  readonly origPageName: string;
}

export interface IMetaUpdateIntentId {
  readonly type: ACTIONS.META_UPDATE_INTENT_ID;
  readonly intentId: string;
  readonly mainOptionName: string;
}

export interface IMetaUpdateDealerOfferParams {
  readonly type: ACTIONS.META_UPDATE_DEALER_OFFER_PARAMS;
  readonly dealerOfferParams: DealerOfferParams;
}

export interface IMetaUpdateArticleUri {
  readonly type: ACTIONS.META_UPDATE_ARTICLE_URI;
  readonly articleUri: string;
}
export type TMetaActions =
  | IMetaUpdatePageUrl
  | IMetaUpdateIntentId
  | IMetaUpdateOrigParams
  | IMetaUpdateDealerOfferParams
  | IMetaUpdateArticleUri;

// root actions
export interface IRootResetChatbotState {
  readonly type: ACTIONS.RESET_CHATBOT_STATE;
  readonly initialChatbotState: Readonly<Partial<TRootState>> | StartChatInitialData;
}

export type TRootActions = IRootResetChatbotState;

export type TSolution = SearchArticlesItem;

export type TActions = TModalActions | TMetaActions | TInputsFocusActions | TRootActions;
